import { useCallback, useEffect, useState } from 'react'
import type { KeyboardEvent } from 'react'

const COARSE_POINTER_QUERY = '(pointer: coarse)'

/**
 * Whether the primary pointer is coarse (touch) rather than fine (mouse /
 * trackpad). On a phone the on-screen keyboard's Enter key is the only way
 * to insert a line break, so a chat composer must not hijack it there — see
 * `useSendOnEnter` below.
 */
export function useCoarsePointer(): boolean {
    const [coarse, setCoarse] = useState<boolean>(() =>
        typeof window !== 'undefined' && typeof window.matchMedia === 'function'
            ? window.matchMedia(COARSE_POINTER_QUERY).matches
            : false,
    )

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
        const mql = window.matchMedia(COARSE_POINTER_QUERY)
        const onChange = () => setCoarse(mql.matches)
        mql.addEventListener('change', onChange)

        return () => mql.removeEventListener('change', onChange)
    }, [])

    return coarse
}

/**
 * Chat-style composer rule: Enter sends, Shift+Enter inserts a newline,
 * Cmd/Ctrl+Enter also sends. Mirrors the muscle memory of Slack, WhatsApp
 * Web, Messenger, Intercom and Crisp — the right call for a short-turn chat
 * thread. Document-style editors (BlockNote, `IssueComments.tsx`) keep
 * Cmd/Ctrl+Enter-only, where a plain Enter must stay a paragraph break.
 *
 * On a coarse-pointer device (phone/tablet touch keyboard) a plain Enter is
 * left alone — the on-screen keyboard has no other way to add a line break —
 * but Cmd/Ctrl+Enter still sends, for a tablet with a hardware keyboard
 * attached. An IME composition in progress (candidate selection on a
 * Japanese/Chinese/Korean keyboard) is never treated as a send.
 *
 * `enabled` gates the actual `send()` call (empty draft, a pending request,
 * an unreachable recipient) without changing the newline behaviour: a
 * disabled Enter/Cmd+Enter is swallowed silently (no newline, no send, no
 * validation error), while Shift+Enter and Alt+Enter still fall through to
 * the browser regardless of `enabled`.
 */
export function useSendOnEnter({ send, enabled }: { send: () => void; enabled: boolean }) {
    const isCoarsePointer = useCoarsePointer()

    return useCallback(
        (e: KeyboardEvent<HTMLTextAreaElement>) => {
            // IME composition (candidate still being picked): never send, and
            // never touch the newline behaviour the input method controls.
            if (e.nativeEvent.isComposing || e.key === 'Process') return
            if (e.key !== 'Enter') return

            const withModifier = e.metaKey || e.ctrlKey

            // Touch keyboards: a bare Enter is the only way to add a line
            // break, so leave it alone. A hardware-keyboard modifier still
            // sends (e.g. a tablet with a keyboard case).
            if (isCoarsePointer && !withModifier) return

            // Shift+Enter and (unmodified) Alt+Enter both fall through as a
            // newline, on every pointer type.
            if (e.shiftKey) return
            if (e.altKey && !withModifier) return

            e.preventDefault()
            if (enabled) send()
        },
        [isCoarsePointer, enabled, send],
    )
}
